import { memo } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faClock,
  faStar,
  faHandshake,
  faPause,
} from "@fortawesome/free-solid-svg-icons";
import { useCarousel } from "../../hooks/useCarousel";
import "../../style/ListingCard.css";

const getStatusInfo = (status, remainingQty, quantity) => {
  if (status?.toLowerCase() === "paused") return { label: "Paused", color: "#6b7280", icon: faPause, pct: null };
  const pct = quantity > 0 ? Math.round((remainingQty / quantity) * 100) : 0;
  const isNew = status?.toLowerCase() === "new" && pct === 100;
  if (isNew) return { label: "New", color: "#1976D2", icon: faStar, pct: null };
  if (pct === 0) return { label: "Donated", color: "#10b981", icon: faHandshake, pct: null };
  if (pct < 20) return { label: "Almost Out", color: "#c0392b", icon: null, pct };
  if (pct < 40) return { label: "Low", color: "#d97706", icon: null, pct };
  return { label: "Available", color: "#669d2e", icon: null, pct };
};

const formatShortDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const [year, month, day] = dateString.split(/[-T]/);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      month: "short", day: "numeric",
    });
  } catch { return dateString; }
};

const getDaysUntilDeadline = (dateString) => {
  if (!dateString) return null;
  try {
    const [year, month, day] = dateString.split(/[-T]/);
    const deadline = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadline.setHours(0, 0, 0, 0);
    return Math.ceil((deadline - today) / 86400000);
  } catch { return null; }
};

function ListingCard({ listing, darkMode, onClick }) {
  const { t } = useTranslation();
  const {
    images, idx, hasMultiple, prev, next, goTo,
    lightboxUrl, openLightbox, closeLightbox,
  } = useCarousel(listing.images);

  const { label: statusLabel, color: statusColor, icon: statusIcon, pct: statusPct } =
    getStatusInfo(listing.status, listing.remaining_qty, listing.quantity);

  const daysLeft = getDaysUntilDeadline(listing.latest_pickup);
  const isUrgent = daysLeft !== null && daysLeft >= 0 && daysLeft <= 3;
  const isOverdue = daysLeft !== null && daysLeft < 0;
  const isDuplicate = !!listing.duplicated_from;

  return (
    <>
    <div
      className={`lc ${darkMode ? "lc--dark" : "lc--light"}`}
      onClick={() => onClick?.(listing)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(listing); }
      }}
    >
      {/* hero image */}
      <div className="lc__img">
        {images.length > 0
          ? (
            <div
              className="lc__track"
              style={{ transform: `translateX(-${idx * 100}%)` }}
              onClick={openLightbox}
            >
              {images.map((src, i) => (
                <img key={i} src={src} alt={listing.item_name || listing.material} onError={e => { e.target.style.display = "none"; }} />
              ))}
            </div>
          )
          : <div className="lc__img-placeholder" />
        }

        {/* status badge */}
        <span className="lc__badge" style={{ backgroundColor: statusColor }}>
          {statusIcon && <FontAwesomeIcon icon={statusIcon} />}
          {t(statusLabel)}
          {statusPct !== null && <span className="lc__badge-pct">{statusPct}%</span>}
        </span>

        {isDuplicate && (
          <span className="lc__badge lc__badge--dup">{t("Duplicated")}</span>
        )}

        {/* carousel controls */}
        {hasMultiple && (
          <>
            <button className="lc__nav lc__nav--prev" onClick={prev} aria-label="Previous image" disabled={idx === 0}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button className="lc__nav lc__nav--next" onClick={next} aria-label="Next image" disabled={idx === images.length - 1}>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <div className="lc__dots">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`lc__dot ${i === idx ? "lc__dot--active" : ""}`}
                  onClick={(e) => goTo(e, i)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* card body */}
      <div className="lc__body">
        {listing.material_category && (
          <span className="lc__category">{t(listing.material_category).toUpperCase()}</span>
        )}
        <h3 className="lc__title">
          {listing.material || "—"}
          {listing.item_name && (
            <span className="lc__subtitle"> ({listing.item_name})</span>
          )}
        </h3>

        <div className="lc__metrics">
          <div className="lc__metric">
            <span className="lc__metric-label">{t("Quantity")}</span>
            <span className="lc__metric-value">
              {listing.remaining_qty ?? listing.quantity ?? 0}
              <span className="lc__metric-unit"> lbs</span>
              {listing.remaining_qty !== listing.quantity && listing.remaining_qty != null && (
                <span className="lc__metric-total"> / {listing.quantity}</span>
              )}
            </span>
          </div>
          <div className="lc__metric">
            <span className="lc__metric-label">{t("Est. Value")}</span>
            <span className="lc__metric-value">
              ${Number(listing.cost || 0).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="lc__footer">
          <div className="lc__deadline">
            <FontAwesomeIcon icon={faClock} className="lc__clock" />
            <div>
              <span className="lc__deadline-label">{t("Pickup Deadline")}</span>
              <span className={`lc__deadline-val ${isOverdue ? "lc__deadline-val--overdue" : ""}`}>
                {formatShortDate(listing.latest_pickup)}
                {isUrgent && (
                  <span className="lc__urgency">
                    {daysLeft === 0 ? t("Today") : `${daysLeft}d`}
                  </span>
                )}
                {isOverdue && (
                  <span className="lc__urgency lc__urgency--overdue">{t("Overdue")}</span>
                )}
              </span>
            </div>
          </div>
          <button className="lc__arrow" aria-label="View listing">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    </div>
    {lightboxUrl && createPortal(
      <div className="lc__lightbox" onClick={closeLightbox}>
        <img src={lightboxUrl} alt="preview" onClick={e => e.stopPropagation()} />
        <button className="lc__lightbox-close" onClick={closeLightbox}>×</button>
      </div>,
      document.body
    )}
    </>
  );
}

ListingCard.propTypes = {
  listing: PropTypes.object.isRequired,
  darkMode: PropTypes.bool,
  onClick: PropTypes.func,
};

export default memo(ListingCard);
